/**
 * Archetype Classification Engine — Phase 1
 *
 * Classifies each uploaded TeamDraft into a primary construction archetype
 * and an optional secondary label, using the same strategy logic as the
 * rosterOS Chrome extension (src/data/strategies.ts).
 *
 * RB archetypes (primary axis, matching extension IDs):
 *   extreme_zero_rb | zero_rb | hero_rb | robust_rb | heavy_rb | hyperfragile_rb
 *
 * WR archetypes (secondary axis):
 *   zero_wr | hero_wr | wr_heavy | double_wr_start
 *
 * QB timing (tertiary context):
 *   elite_qb | mid_qb | late_qb
 *
 * Overrides (checked first):
 *   stars_and_scrubs | balanced
 */

import type { TeamDraft, TeamPlayer } from "../types/draft";

// ── Archetype IDs (matching extension strategy IDs) ───────────────

export type RBArchetype =
  | "extreme_zero_rb"
  | "zero_rb"
  | "hero_rb"
  | "robust_rb"
  | "heavy_rb"
  | "hyperfragile_rb";

export type WRArchetype =
  | "zero_wr"
  | "hero_wr"
  | "wr_heavy"
  | "double_wr_start";

export type QBArchetype = "elite_qb" | "mid_qb" | "late_qb";

export type OverrideArchetype = "stars_and_scrubs" | "balanced";

export type PrimaryArchetype = RBArchetype | OverrideArchetype;

/** Human-readable display names, matching extension strategy names */
export const ARCHETYPE_LABELS: Record<PrimaryArchetype | WRArchetype | QBArchetype, string> = {
  extreme_zero_rb:  "Extreme Zero RB",
  zero_rb:          "Zero RB",
  hero_rb:          "Hero RB",
  robust_rb:        "Robust RB",
  heavy_rb:         "Heavy RB",
  hyperfragile_rb:  "Hyperfragile RB",
  stars_and_scrubs: "Stars & Scrubs",
  balanced:         "Balanced",
  zero_wr:          "Zero WR",
  hero_wr:          "Hero WR",
  wr_heavy:         "Heavy WR",
  double_wr_start:  "Double WR Start",
  elite_qb:         "Elite QB",
  mid_qb:           "Mid-Round QB",
  late_qb:          "Late-Round QB",
};

export interface ArchetypeResult {
  draftId: string;
  primaryArchetype: PrimaryArchetype;
  primaryLabel: string;
  /** Optional secondary label (WR strategy or QB timing) */
  secondaryArchetype: WRArchetype | QBArchetype | null;
  secondaryLabel: string | null;
  /** Descriptive sentence, e.g. "Hero RB · Late-Round QB" */
  summary: string;
}

// ── Portfolio-level aggregation ───────────────────────────────────

export interface ArchetypeCount {
  archetype: PrimaryArchetype;
  label: string;
  count: number;
  rate: number;
}

export interface PortfolioArchetypeAnalysis {
  totalTeams: number;
  /** Distribution across primary archetypes, sorted by count desc */
  distribution: ArchetypeCount[];
  /** Dominant archetype (highest count) */
  dominantArchetype: PrimaryArchetype | null;
  /** True if > 70% of teams share the same primary archetype */
  isConcentrated: boolean;
  /** Per-team results for downstream use (3b grading, 5a scoring) */
  teamResults: ArchetypeResult[];
}

// ── Internal computation helpers ──────────────────────────────────

const DEFAULT_TEAM_COUNT = 12; // Standard Underdog best-ball

/**
 * Compute the draft round for an absolute pick number.
 * Underdog best-ball: 12 teams × 18 rounds = 216 picks.
 */
function pickToRound(pickNumber: number, teamCount = DEFAULT_TEAM_COUNT): number {
  return Math.ceil(pickNumber / teamCount);
}

/**
 * Build a cumulative count matrix:
 *   countMatrix[pos][round] = # of players at `pos` drafted through that round.
 */
function buildCountMatrix(
  players: TeamPlayer[],
  teamCount = DEFAULT_TEAM_COUNT
): Record<string, Record<number, number>> {
  const MAX_ROUND = 18;

  // Step 1: Count picks per position per round
  const raw: Record<string, Record<number, number>> = {};
  for (const p of players) {
    const pos = p.position;
    const pick = parseInt(p.pickNumber, 10);
    if (!pick || !pos) continue;
    const round = pickToRound(pick, teamCount);
    if (!raw[pos]) raw[pos] = {};
    raw[pos][round] = (raw[pos][round] ?? 0) + 1;
  }

  // Step 2: Make cumulative
  const cumulative: Record<string, Record<number, number>> = {};
  for (const pos of Object.keys(raw)) {
    cumulative[pos] = {};
    let running = 0;
    for (let r = 1; r <= MAX_ROUND; r++) {
      running += raw[pos][r] ?? 0;
      cumulative[pos][r] = running;
    }
  }
  return cumulative;
}

/** Count players at `pos` drafted through (and including) `throughRound`. */
function posCountThrough(
  countMatrix: Record<string, Record<number, number>>,
  pos: string,
  throughRound: number
): number {
  return countMatrix[pos]?.[throughRound] ?? 0;
}

/** Total picks in rounds 1–N */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function totalPicksThrough(players: TeamPlayer[], throughRound: number, teamCount = DEFAULT_TEAM_COUNT): number {
  return players.filter(p => {
    const pick = parseInt(p.pickNumber, 10);
    return pick && pickToRound(pick, teamCount) <= throughRound;
  }).length;
}

// ── Stars & Scrubs detection ──────────────────────────────────────

/**
 * Stars & Scrubs: 3+ players in rounds 1–4, AND all remaining picks
 * are round 10 or later.
 */
function isStarsAndScrubs(players: TeamPlayer[], teamCount = DEFAULT_TEAM_COUNT): boolean {
  const earlyPicks = players.filter(p => {
    const pick = parseInt(p.pickNumber, 10);
    return pick && pickToRound(pick, teamCount) <= 4;
  });
  if (earlyPicks.length < 3) return false;

  const latePicks = players.filter(p => {
    const pick = parseInt(p.pickNumber, 10);
    return pick && pickToRound(pick, teamCount) > 4;
  });
  return latePicks.every(p => pickToRound(parseInt(p.pickNumber, 10), teamCount) >= 10);
}

// ── Balanced detection ────────────────────────────────────────────

/**
 * Balanced: no extreme positional tilt; no RB archetype fires and no
 * WR archetype fires. Used as the catch-all fallback.
 */

// ── Primary archetype classification ─────────────────────────────

function classifyRBArchetype(
  matrix: Record<string, Record<number, number>>,
  totalRounds: number
): RBArchetype | null {
  const cnt = (r: number) => posCountThrough(matrix, "RB", r);

  if (totalRounds < 3) return null; // Not enough picks to classify

  // Most specific checks first (heavy_rb, hyperfragile, then broad ones)
  if (cnt(6) >= 4) return "heavy_rb";
  if (cnt(6) === 3 && (matrix["RB"]?.[18] ?? cnt(18)) <= 4) return "hyperfragile_rb";
  if (cnt(5) >= 2) return "robust_rb";
  if (cnt(5) === 1) return "hero_rb";
  if (cnt(7) === 0 && totalRounds >= 5) return "extreme_zero_rb";
  if (cnt(5) === 0 && totalRounds >= 3) return "zero_rb";

  return null;
}

function classifyWRArchetype(
  matrix: Record<string, Record<number, number>>,
  totalRounds: number
): WRArchetype | null {
  const cnt = (r: number) => posCountThrough(matrix, "WR", r);

  if (totalRounds < 3) return null;
  if (cnt(2) >= 2) return "double_wr_start";
  if (cnt(6) >= 4) return "wr_heavy";
  if (cnt(5) === 1) return "hero_wr";
  if (cnt(5) === 0) return "zero_wr";

  return null;
}

function classifyQBArchetype(
  matrix: Record<string, Record<number, number>>,
  totalRounds: number
): QBArchetype | null {
  const cnt = (r: number) => posCountThrough(matrix, "QB", r);

  if (totalRounds < 6) return null;
  if (cnt(4) >= 1) return "elite_qb";
  if (cnt(9) >= 1) return "mid_qb";
  return "late_qb";
}

// ── Public API ────────────────────────────────────────────────────

/**
 * Classify a single uploaded team into a primary + secondary archetype.
 *
 * @param team       The TeamDraft to classify
 * @param teamCount  Draft size (default 12; used to compute round from pick number)
 */
export function classifyTeamArchetype(
  team: TeamDraft,
  teamCount = DEFAULT_TEAM_COUNT
): ArchetypeResult {
  const { players } = team;
  const matrix = buildCountMatrix(players, teamCount);

  // Compute how many rounds this team's picks span (proxy for draft completeness)
  const pickNums = players
    .map(p => parseInt(p.pickNumber, 10))
    .filter(n => n > 0);
  const maxPick = pickNums.length > 0 ? Math.max(...pickNums) : 0;
  const totalRounds = maxPick ? pickToRound(maxPick, teamCount) : 0;

  // --- Override checks ---
  let primary: PrimaryArchetype;
  if (isStarsAndScrubs(players, teamCount)) {
    primary = "stars_and_scrubs";
  } else {
    // RB archetype is the primary axis
    const rbArchetype = classifyRBArchetype(matrix, totalRounds);
    primary = rbArchetype ?? "balanced";
  }

  // Secondary axis: WR archetype, then QB timing as fallback
  const wrArchetype = classifyWRArchetype(matrix, totalRounds);
  const qbArchetype = classifyQBArchetype(matrix, totalRounds);

  // Prefer WR as secondary; use QB timing if no WR archetype fires
  const secondary: WRArchetype | QBArchetype | null = wrArchetype ?? qbArchetype ?? null;

  const primaryLabel = ARCHETYPE_LABELS[primary];
  const secondaryLabel = secondary ? ARCHETYPE_LABELS[secondary] : null;

  const summary = secondaryLabel
    ? `${primaryLabel} · ${secondaryLabel}`
    : primaryLabel;

  return {
    draftId: team.draftId,
    primaryArchetype: primary,
    primaryLabel,
    secondaryArchetype: secondary,
    secondaryLabel,
    summary,
  };
}

/**
 * Classify all uploaded teams and compute portfolio-level archetype distribution.
 */
export function analyzePortfolioArchetypes(
  teams: TeamDraft[],
  teamCount = DEFAULT_TEAM_COUNT
): PortfolioArchetypeAnalysis {
  if (teams.length === 0) {
    return {
      totalTeams: 0,
      distribution: [],
      dominantArchetype: null,
      isConcentrated: false,
      teamResults: [],
    };
  }

  const teamResults = teams.map(t => classifyTeamArchetype(t, teamCount));
  const totalTeams = teams.length;

  // Tally counts per primary archetype
  const counts = new Map<PrimaryArchetype, number>();
  for (const r of teamResults) {
    counts.set(r.primaryArchetype, (counts.get(r.primaryArchetype) ?? 0) + 1);
  }

  const distribution: ArchetypeCount[] = Array.from(counts.entries())
    .map(([archetype, count]) => ({
      archetype,
      label: ARCHETYPE_LABELS[archetype],
      count,
      rate: count / totalTeams,
    }))
    .sort((a, b) => b.count - a.count);

  const dominantArchetype = distribution.length > 0 ? distribution[0].archetype : null;
  const dominantRate = distribution.length > 0 ? distribution[0].rate : 0;
  const isConcentrated = dominantRate > 0.7;

  return {
    totalTeams,
    distribution,
    dominantArchetype,
    isConcentrated,
    teamResults,
  };
}

// ── Per-position archetypes (exhaustive — every team gets exactly one
//    bucket per position, so each position's distribution sums to 100%) ──

/** Fixed display order of buckets for each position. */
export const POSITION_ARCHETYPE_ORDER: Record<string, string[]> = {
  QB: ["Early QB", "Mid QB", "Late QB", "No QB"],
  RB: ["Zero RB", "Hero RB", "Robust RB", "Heavy RB"],
  WR: ["Zero WR", "Hero WR", "Double-Up WR", "WR-Heavy"],
  TE: ["Elite TE", "Mid TE", "Late TE", "No TE"],
};

/** Plain-English definition of each bucket (matches the thresholds below). */
export const POSITION_ARCHETYPE_DESCRIPTIONS: Record<string, string> = {
  "Early QB": "Took your first QB in the first 6 rounds.",
  "Mid QB": "Took your first QB in rounds 7 to 11.",
  "Late QB": "Took your first QB in round 12 or later.",
  "No QB": "No QB drafted.",
  "Zero RB": "No RB in the first 5 rounds.",
  "Hero RB": "One RB in the first 5 rounds.",
  "Robust RB": "Two RBs in the first 5 rounds.",
  "Heavy RB": "Three or more RBs in the first 5 rounds.",
  "Zero WR": "No WR in the first 5 rounds.",
  "Hero WR": "One WR in the first 5 rounds.",
  "Double-Up WR": "Two WRs in the first 5 rounds.",
  "WR-Heavy": "Three or more WRs in the first 5 rounds.",
  "Elite TE": "Took your first TE in the first 6 rounds.",
  "Mid TE": "Took your first TE in rounds 7 to 11.",
  "Late TE": "Took your first TE in round 12 or later.",
  "No TE": "No TE drafted.",
};

function countPosThroughRound(players: TeamPlayer[], pos: string, round: number, teamCount: number): number {
  return players.filter(p => {
    const pick = parseInt(p.pickNumber, 10);
    return p.position === pos && pick > 0 && pickToRound(pick, teamCount) <= round;
  }).length;
}

function firstPosRound(players: TeamPlayer[], pos: string, teamCount: number): number | null {
  const rounds = players
    .filter(p => p.position === pos && parseInt(p.pickNumber, 10) > 0)
    .map(p => pickToRound(parseInt(p.pickNumber, 10), teamCount));
  return rounds.length ? Math.min(...rounds) : null;
}

/**
 * Classify a team's build for a single position.
 *  - RB/WR: by how many you draft through round 5 (Zero / Hero / Robust|Double-Up / Heavy).
 *  - QB/TE: by the round you take your first (Early|Elite / Mid / Late).
 */
export function classifyPositionArchetype(
  players: TeamPlayer[],
  pos: string,
  teamCount = DEFAULT_TEAM_COUNT,
): string {
  if (pos === "RB" || pos === "WR") {
    const c = countPosThroughRound(players, pos, 5, teamCount);
    if (pos === "RB") return c === 0 ? "Zero RB" : c === 1 ? "Hero RB" : c === 2 ? "Robust RB" : "Heavy RB";
    return c === 0 ? "Zero WR" : c === 1 ? "Hero WR" : c === 2 ? "Double-Up WR" : "WR-Heavy";
  }
  const r = firstPosRound(players, pos, teamCount);
  if (pos === "QB") return r === null ? "No QB" : r <= 6 ? "Early QB" : r <= 11 ? "Mid QB" : "Late QB";
  return r === null ? "No TE" : r <= 6 ? "Elite TE" : r <= 11 ? "Mid TE" : "Late TE";
}

export interface PositionArchetypeDist {
  position: string;
  buckets: { label: string; count: number; rate: number }[];
}

/**
 * Per-position archetype distribution across the portfolio. Returns one entry
 * per position (QB/RB/WR/TE); buckets follow POSITION_ARCHETYPE_ORDER and omit
 * any with zero teams. Each position's buckets sum to the team total.
 */
export function analyzePositionArchetypes(
  teams: TeamDraft[],
  teamCount = DEFAULT_TEAM_COUNT,
): PositionArchetypeDist[] {
  const total = teams.length || 1;
  return Object.keys(POSITION_ARCHETYPE_ORDER).map(pos => {
    const counts = new Map<string, number>();
    for (const t of teams) {
      const label = classifyPositionArchetype(t.players, pos, teamCount);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    const buckets = POSITION_ARCHETYPE_ORDER[pos]
      .filter(label => (counts.get(label) ?? 0) > 0)
      .map(label => ({ label, count: counts.get(label)!, rate: counts.get(label)! / total }));
    return { position: pos, buckets };
  });
}
